import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { bookDmitTestSession, verifyDmitTestPayment } from './DmitTestAPi';
import { toast } from 'react-toastify';
import { X, User, Mail, Phone, Calendar, Brain, Clock, IndianRupee } from 'lucide-react';
import { userApi } from '../../../config';
import BookingConfirmationModal from '../../BookingConfirmationModal';

interface SessionData {
    _id: string;
    date: string;
    timing: string;
    price: number;
}

const DmitTestBooking: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { sessionData } = (location.state || {}) as { sessionData: SessionData };

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        age: '',
        gender: '',
        phone: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userDetails, setUserDetails] = useState<any>(null);

    // Confirmation modal state
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [pendingBookingData, setPendingBookingData] = useState<any>(null);

    // 1. Script Loading Effect
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        return () => {
            document.body.removeChild(script);
        }
    }, []);

    useEffect(() => {
        const fetchUserDetails = async () => {
            const token = sessionStorage.getItem("userAccessToken");
            if (token) {
                try {
                    const res = await userApi.get<{user: any}>('/get-user-profile', { headers: { Authorization: `Bearer ${token}` } });
                    setUserDetails(res.data.user);
                } catch (error) {
                    console.error('Failed to fetch user details', error);
                }
            }
        };
        fetchUserDetails();
    }, []);

    useEffect(() => {
        if (userDetails) {
            setFormData({
                name: `${userDetails.firstname || ''} ${userDetails.lastname || ''}`.trim(),
                email: userDetails.useremail || '',
                phone: userDetails.userphone || '',
                age: userDetails.age || '',
                gender: userDetails.gender || 'Male',
            });
        }
    }, [userDetails]);

    if (!sessionData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 text-center p-4">
                <h2 className="text-2xl font-bold text-red-600 mb-4">Booking session not found.</h2>
                <button onClick={() => navigate('/Dmit-tests')} className="bg-teal-500 text-white font-bold py-3 px-6 rounded-lg">Go to DMIT Tests</button>
            </div>
        );
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Safety check for Razorpay script
        if (!(window as any).Razorpay) {
            toast.error("Razorpay SDK failed to load. Are you online?");
            return;
        }

        // Prepare booking details for confirmation modal
        const bookingDetails = {
            serviceName: 'DMIT Full Brain Analysis',
            serviceType: 'dmit' as const,
            date: sessionData.date,
            timeSlot: sessionData.timing,
            amount: sessionData.price,
            userDetails: {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                age: formData.age,
                gender: formData.gender,
            },
        };

        const queryParams = new URLSearchParams(window.location.search);
        const urlVendorId = queryParams.get("vendorId") || queryParams.get("mentorId");
        if (urlVendorId) {
            sessionStorage.setItem("referralVendorId", urlVendorId);
        }
        const vendorId = urlVendorId || sessionStorage.getItem("referralVendorId") || undefined;

        // Prepare booking data for API call
        const bookingPayload = {
            sessionId: sessionData._id,
            name: formData.name,
            email: formData.email,
            age: formData.age,
            gender: formData.gender,
            phone: formData.phone,
            totalAmount: sessionData.price,
            bookingDate: sessionData.date,
            vendorId: vendorId,
        };

        setPendingBookingData({ bookingPayload, bookingDetails });
        setShowConfirmationModal(true);
    };

    const handleConfirmBooking = async () => {
        if (!pendingBookingData) return;

        setShowConfirmationModal(false);
        setIsSubmitting(true);

        const { bookingPayload } = pendingBookingData;

        try {
            const response: any = await bookDmitTestSession(bookingPayload);

            if (response && response.data) {
                const { razorpayOrderId, amount, currency, bookingId } = response.data;

                const options = {
                    key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                    amount: amount,
                    currency: currency,
                    name: 'Metafit Wellness',
                    description: 'DMIT Test Booking',
                    order_id: razorpayOrderId,
                    handler: async (paymentResponse: any) => {
                        const verificationData = {
                            razorpay_order_id: paymentResponse.razorpay_order_id,
                            razorpay_payment_id: paymentResponse.razorpay_payment_id,
                            razorpay_signature: paymentResponse.razorpay_signature,
                            bookingId: bookingId,
                        };

                        const verifyResponse: any = await verifyDmitTestPayment(verificationData);

                        if (verifyResponse.success) {
                            toast.success('Booking confirmed successfully!');
                            navigate('/profile');
                        } else {
                            toast.error(verifyResponse.message || 'Payment verification failed.');
                        }
                        setIsSubmitting(false);
                    },
                    prefill: {
                        name: formData.name,
                        email: formData.email,
                        contact: formData.phone,
                    },
                    theme: { color: '#14B8A6' },
                    modal: {
                        ondismiss: () => {
                            toast.warn('Payment was not completed.');
                            setIsSubmitting(false);
                        }
                    }
                };

                // Fixed constructor call
                const rzp = new (window as any).Razorpay(options);
                rzp.open();

            } else {
                toast.error(response.message || 'Failed to initiate booking.');
                setIsSubmitting(false);
            }
        } catch (error) {
            toast.error('An unexpected error occurred.');
            console.error('Booking Error:', error);
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 font-sans">
            <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full grid grid-cols-1 md:grid-cols-2 overflow-hidden">
                <div className="bg-teal-600 text-white p-8 flex flex-col">
                    <h2 className="text-3xl font-black mb-6">Confirm Your Booking</h2>
                    <div className="space-y-4 text-teal-100">
                        <div className="flex items-center gap-3">
                            <Brain className="h-5 w-5" />
                            <span className="font-semibold">DMIT Full Brain Analysis</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <Calendar className="h-5 w-5" />
                            <span className="font-semibold">{new Date(sessionData.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <Clock className="h-5 w-5" />
                            <span className="font-semibold">{sessionData.timing}</span>
                        </div>
                    </div>
                    <div className="mt-auto pt-8">
                        <p className="text-sm text-teal-200">Total Amount</p>
                        <p className="text-5xl font-extrabold flex items-center">
                            <IndianRupee className="h-8 w-8 mr-1" />
                            {sessionData.price}
                        </p>
                    </div>
                </div>

                <div className="p-8">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-2xl font-bold text-gray-800">Enter Your Details</h3>
                        <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600">
                            <X className="h-6 w-6" />
                        </button>
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="text-sm font-bold text-gray-600 mb-1 block">Full Name</label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-bold text-gray-600 mb-1 block">Email Address</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-bold text-gray-600 mb-1 block">Age</label>
                                <input type="number" name="age" value={formData.age} onChange={handleChange} required className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
                            </div>
                            <div>
                                <label className="text-sm font-bold text-gray-600 mb-1 block">Gender</label>
                                <select name="gender" value={formData.gender} onChange={handleChange} required className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none">
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-bold text-gray-600 mb-1 block">Phone Number</label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-teal-500 text-white font-bold py-4 rounded-lg hover:bg-teal-600 transition-all shadow-lg active:scale-95 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? 'Processing...' : `Pay ₹${sessionData.price} to Confirm`}
                        </button>
                    </form>
                </div>
            </div>
        </div>

        {/* Booking Confirmation Modal */}
        {showConfirmationModal && pendingBookingData && (
            <BookingConfirmationModal
                isOpen={showConfirmationModal}
                onClose={() => setShowConfirmationModal(false)}
                onConfirm={handleConfirmBooking}
                bookingDetails={pendingBookingData.bookingDetails}
                isProcessing={isSubmitting}
            />
        )}
        </>
    );
};

export default DmitTestBooking;
